import { useMemo, useState } from 'react';
import { PMPartsAndProcedure } from '../../types/pmTypes';
import { ServiceProcedure } from '../../types/companyResourceTypes';
import { consumableCatalogItems, filterServiceItems, parseProcedureConsumableQuantity, sortServiceItemsForListing } from '../../utils/companyResourceUtils';
import { CustomModal } from '../CustomModal';
import { Btn, IconPlus, InputField, Select } from '../ui';

interface ServiceProcedureConsumablePickerProps {
    procedure: ServiceProcedure;
    catalog: PMPartsAndProcedure[];
    linkedServiceItemIds: Set<string>;
    savingItemId: string | null;
    onClose: () => void;
    onSelect: (item: PMPartsAndProcedure, quantity: number) => void;
}

export const ServiceProcedureConsumablePicker = ({
    procedure,
    catalog,
    linkedServiceItemIds,
    savingItemId,
    onClose,
    onSelect,
}: ServiceProcedureConsumablePickerProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [pendingItem, setPendingItem] = useState<PMPartsAndProcedure | null>(null);
    const [quantity, setQuantity] = useState('');
    const [quantityError, setQuantityError] = useState('');

    const available = useMemo(() => {
        const consumables = consumableCatalogItems(catalog).filter(
            (item) => !linkedServiceItemIds.has(item.id),
        );
        return filterServiceItems(consumables, searchQuery, categoryFilter);
    }, [catalog, linkedServiceItemIds, searchQuery, categoryFilter]);

    const categories = useMemo(() => {
        const names = new Set(
            consumableCatalogItems(catalog)
                .filter((item) => !linkedServiceItemIds.has(item.id))
                .map((item) => item.category || 'Uncategorized'),
        );
        return [...names].sort();
    }, [catalog, linkedServiceItemIds]);

    const grouped = useMemo(() => {
        const byCategory: Record<string, PMPartsAndProcedure[]> = {};
        for (const item of available) {
            const cat = item.category || 'Uncategorized';
            if (!byCategory[cat]) byCategory[cat] = [];
            byCategory[cat].push(item);
        }
        for (const cat of Object.keys(byCategory)) {
            byCategory[cat] = sortServiceItemsForListing(byCategory[cat]);
        }
        return Object.entries(byCategory).sort(([a], [b]) => a.localeCompare(b));
    }, [available]);

    const pendingUm = pendingItem?.unitOfMeasure?.trim() || '';

    const handleBackToList = () => {
        setPendingItem(null);
        setQuantity('');
        setQuantityError('');
    };

    const handleConfirmQuantity = () => {
        if (!pendingItem) return;
        const parsed = parseProcedureConsumableQuantity(quantity);
        if (parsed == null) {
            setQuantityError('Enter a quantity greater than zero.');
            return;
        }
        onSelect(pendingItem, parsed);
        handleBackToList();
    };

    return (
        <CustomModal
            visible
            onDismiss={onClose}
            header={`Add consumable to ${procedure.itemName}`}
            size="large"
            preferBodyFocus
            describedById="procedure-consumable-picker-help"
            footer={
                <div className="modal-footer-actions">
                    {pendingItem ? (
                        <>
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Back"
                                buttonOnClick={handleBackToList}
                                buttonDisabled={Boolean(savingItemId)}
                            />
                            <Btn
                                variant="primary"
                                buttonText={savingItemId === pendingItem.id ? 'Adding...' : 'Add'}
                                buttonOnClick={handleConfirmQuantity}
                                buttonDisabled={Boolean(savingItemId)}
                            />
                        </>
                    ) : (
                        <Btn variant="secondary-outlined" buttonText="Done" buttonOnClick={onClose} />
                    )}
                </div>
            }
        >
            {pendingItem ? (
                <div className="cr-consumable-qty-step">
                    <p id="procedure-consumable-picker-help" className="cr-field-helper">
                        Assign a flat-rate quantity of this consumable for the procedure. The unit of measure
                        comes from the Service Item pricing.
                    </p>
                    <p className="cr-consumable-qty-item">
                        <span className="cr-consumable-picker-name">{pendingItem.itemName}</span>
                        {(pendingItem.manufacturer || pendingItem.partNumber) && (
                            <span className="cr-consumable-picker-meta">
                                {[pendingItem.manufacturer, pendingItem.partNumber].filter(Boolean).join(' · ')}
                            </span>
                        )}
                    </p>
                    <div className="cr-consumable-qty-row">
                        <InputField
                            inputId="procedure-consumable-quantity"
                            className="cr-consumable-qty-field"
                            label="Quantity"
                            type="number"
                            inputMode="decimal"
                            step="any"
                            min={0}
                            value={quantity}
                            onChange={(next) => {
                                setQuantity(next);
                                if (quantityError) setQuantityError('');
                            }}
                            hasError={Boolean(quantityError)}
                            autoFocus
                            suffix={pendingUm || undefined}
                            suffixId={pendingUm ? 'procedure-consumable-quantity-um' : undefined}
                            ariaDescribedBy={
                                [
                                    quantityError ? 'procedure-consumable-quantity-error' : null,
                                    pendingUm ? 'procedure-consumable-quantity-um' : null,
                                ]
                                    .filter(Boolean)
                                    .join(' ') || undefined
                            }
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleConfirmQuantity();
                                }
                            }}
                        />
                    </div>
                    {quantityError && (
                        <div id="procedure-consumable-quantity-error" className="cr-field-error-text" role="alert">
                            {quantityError}
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <p id="procedure-consumable-picker-help" className="cr-field-helper">
                        Choose a Service Item, then enter the flat-rate quantity used when this procedure is
                        performed. The item is linked, not copied.
                    </p>
                    <div className="cr-toolbar cr-consumable-picker-toolbar">
                        <InputField
                            inputId="procedure-consumable-search"
                            className="cr-consumable-picker-search"
                            type="search"
                            ariaLabel="Search service items"
                            placeholder="Search by name, category, part #, or manufacturer..."
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                        <Select
                            className="cr-consumable-picker-category"
                            ariaLabel="Filter by service item category"
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            placeholder="All Categories"
                            options={[
                                { value: '', label: 'All Categories' },
                                ...categories.map((category) => ({ value: category, label: category })),
                            ]}
                        />
                    </div>

                    {grouped.length === 0 ? (
                        <div className="cr-empty-state">
                            <p className="cr-empty-state-text">
                                {consumableCatalogItems(catalog).length === 0
                                    ? 'No consumable service items yet. Add them on the Service Items tab first.'
                                    : 'No matching service items, or all visible items are already linked.'}
                            </p>
                        </div>
                    ) : (
                        <ul className="cr-consumable-picker-groups">
                            {grouped.map(([cat, items]) => (
                                <li key={cat} className="cr-consumable-picker-group">
                                    <h3 className="cr-consumable-picker-group-title">{cat}</h3>
                                    <ul className="cr-consumable-picker-list">
                                        {items.map((item) => (
                                            <li key={item.id} className="cr-consumable-picker-row">
                                                <div className="cr-consumable-picker-copy">
                                                    <span className="cr-consumable-picker-name">
                                                        {item.itemName}
                                                    </span>
                                                    {(item.partNumber || item.manufacturer || item.unitOfMeasure) && (
                                                        <span className="cr-consumable-picker-meta">
                                                            {[item.manufacturer, item.partNumber, item.unitOfMeasure]
                                                                .filter(Boolean)
                                                                .join(' · ')}
                                                        </span>
                                                    )}
                                                </div>
                                                <Btn
                                                    variant="secondary-outlined"
                                                    buttonSize="sm"
                                                    buttonIcon={<IconPlus />}
                                                    buttonText="Add"
                                                    ariaLabel={`Add ${item.itemName} to ${procedure.itemName}`}
                                                    buttonDisabled={savingItemId === item.id}
                                                    buttonOnClick={() => {
                                                        setPendingItem(item);
                                                        setQuantity('');
                                                        setQuantityError('');
                                                    }}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </CustomModal>
    );
};
